import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full px-3 py-2 rounded-lg bg-[#F6EEDC] border border-[#F1551D]/20 text-[#0D3C4C] placeholder:text-[#aab9bd] focus:outline-none focus:border-[#F1551D]/60 text-sm";

export default function Referrals() {
  const { meta } = useAuth();
  const [data, setData] = useState({ referrals: [], total_points: 0, level: null });
  const [form, setForm] = useState({ referee_name: "", referee_category: "", referee_org: "", referee_role: "", referee_contact: "", relationship: "", contribution_type: "", quality_rating: 3, notes: "" });
  const [saving, setSaving] = useState(false);

  const ctypes = meta?.contribution_types || [];
  const load = () => api.get("/referrals/mine").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selected = ctypes.find((c) => c.id === form.contribution_type);
  const projected = selected ? selected.base * form.quality_rating : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contribution_type) { toast.error("Select a contribution type."); return; }
    setSaving(true);
    try {
      await api.post("/referrals", { ...form, quality_rating: Number(form.quality_rating) });
      toast.success(`Referral logged · +${projected} points earned.`);
      setForm({ ...form, referee_name: "", referee_org: "", referee_role: "", referee_contact: "", relationship: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="rise">
      <div className="mb-6">
        <div className="font-mono-i text-[11px] tracking-[0.3em] text-[#F1551D]/90 uppercase mb-2">Referral & Contributor System</div>
        <h1 className="font-display text-3xl text-[#0D3C4C]">Referrals</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="n360-card p-6 lg:col-span-1 h-fit">
          <div className="text-center mb-5">
            <div className="font-display text-5xl gold-text">{data.total_points}</div>
            <div className="text-sm text-[#5f7d86]">Total Contribution Points</div>
          </div>
          <div className="rounded-lg border border-[#F1551D]/25 bg-[#F1551D]/8 p-4 text-center">
            <div className="text-[11px] uppercase tracking-wider text-[#8598a0]">Contributor Level {data.level?.level}</div>
            <div className="font-display text-lg text-[#B84214]">{data.level?.name}</div>
          </div>
          <div className="mt-5 space-y-2">
            {(meta?.contributor_levels || []).slice().reverse().map((l) => (
              <div key={l.level} className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg border ${data.level?.level === l.level ? "border-[#F1551D]/50 bg-[#F1551D]/10 text-[#B84214]" : "border-[#F1551D]/12 text-[#5f7d86]"}`}>
                <span>L{l.level} · {l.name}</span><span className="font-mono-i">{l.min}+</span>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="n360-card p-6 lg:col-span-2 space-y-4">
          <h3 className="font-display text-lg text-[#0D3C4C]">Submit a Referral</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Referee Name *</div><input required data-testid="ref-name" value={form.referee_name} onChange={set("referee_name")} className={inputCls} /></div>
            <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Category</div><input data-testid="ref-category" value={form.referee_category} onChange={set("referee_category")} className={inputCls} placeholder="Leader / Bridge Builder…" /></div>
            <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Organization</div><input data-testid="ref-org" value={form.referee_org} onChange={set("referee_org")} className={inputCls} /></div>
            <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Role / Designation</div><input data-testid="ref-role" value={form.referee_role} onChange={set("referee_role")} className={inputCls} /></div>
            <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Contact</div><input data-testid="ref-contact" value={form.referee_contact} onChange={set("referee_contact")} className={inputCls} /></div>
            <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Your Relationship</div><input data-testid="ref-relationship" value={form.relationship} onChange={set("relationship")} className={inputCls} /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Contribution Type *</div>
              <select data-testid="ref-type" value={form.contribution_type} onChange={set("contribution_type")} className={inputCls}>
                <option value="" className="bg-[#F6EEDC]">Select…</option>
                {ctypes.map((c) => <option key={c.id} value={c.id} className="bg-[#F6EEDC]">{c.label} ({c.base} base)</option>)}
              </select>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Quality Rating: {form.quality_rating}/5</div>
              <input data-testid="ref-quality" type="range" min={1} max={5} value={form.quality_rating} onChange={set("quality_rating")} className="w-full accent-[#F1551D] mt-2.5" />
            </div>
          </div>
          <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Notes</div><textarea data-testid="ref-notes" value={form.notes} onChange={set("notes")} rows={2} className={inputCls} /></div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-[#5f7d86]">Projected earn: <span className="font-mono-i text-[#B84214]">{projected} pts</span> <span className="text-xs text-[#aab9bd]">(base × quality)</span></div>
            <button disabled={saving} data-testid="ref-submit-btn" className="px-6 py-2.5 rounded-full bg-[#F1551D] text-[#FEF7E7] font-medium hover:bg-[#F0A315] disabled:opacity-60">{saving ? "Submitting…" : "Submit Referral"}</button>
          </div>
        </form>
      </div>

      <div className="n360-card p-6 mt-6">
        <h3 className="font-display text-lg text-[#0D3C4C] mb-4">Your Referrals</h3>
        {data.referrals.length === 0 ? <p className="text-sm text-[#8598a0] text-center py-6">No referrals submitted yet.</p> : (
          <div className="space-y-2">
            {data.referrals.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#F1551D]/12 bg-[#FEF7E7]/50 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-[#154859] text-sm truncate">{r.referee_name} <span className="text-[#8598a0]">· {r.referee_org}</span></div>
                  <div className="text-[11px] text-[#8598a0]">{r.contribution_label} · quality {r.quality_rating}/5</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono-i text-[#B84214] text-sm">+{r.earned_points}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-500/30 text-emerald-300 capitalize">{r.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
